"use client";

import type React from "react";
import { Radio, RadioGroup } from "@boostdev/design-system-components/client";
import { useClientTranslation } from "@/lib/i18n/client";
import { formatPrice } from "@/lib/shop/money";
import type { ProductVariant } from "@/lib/shop/types";
import css from "./VariantPicker.module.css";

export interface VariantPickerProps {
  variants: ProductVariant[];
  selectedId: string;
  onSelect: (variantId: string) => void;
  locale: string;
}

export function VariantPicker({
  variants,
  selectedId,
  onSelect,
  locale,
}: VariantPickerProps): React.JSX.Element {
  const { t } = useClientTranslation(locale, "shop");

  return (
    <RadioGroup legend={t("product.variantLabel")}>
      {variants.map((variant) => {
        const isSoldOut = variant.stock === 0;
        const label = (
          <span className={css.optionLabel}>
            <span className={css.optionTitle}>{variant.title}</span>
            <span className={css.optionPrice}>
              {formatPrice(variant.priceCents, locale)}
            </span>
            {isSoldOut ? (
              <span className={css.soldOut}>{t("product.soldOut")}</span>
            ) : null}
          </span>
        );

        return (
          <Radio
            key={variant.id}
            name="variant"
            label={label}
            value={variant.id}
            checked={variant.id === selectedId}
            disabled={isSoldOut}
            onChange={() => {
              onSelect(variant.id);
            }}
          />
        );
      })}
    </RadioGroup>
  );
}
